import { useState, useCallback, useEffect } from 'react'
import { sileo } from 'sileo'
import { useGlobalContext } from '../../../../../../context/GlobalState'
import { FacturaAPI } from '../../../../../../api/facturas.api'
import FacturaFormSimplified from './createFactura/CreateFactura'
import PrevisualizacionFacturaIndividual from './createFactura/PrevisualizacionFacturaIndividual'
import FirmaEmisionFactura from './createFactura/FirmaEmisionFactura'
import IndexNotaCredito from './createNotaCredito/IndexNotaCredito'

// -------------------- Tipos de documento disponibles --------------------
// -------------------- Tipos de documento disponibles --------------------
const DOC_TYPES = [
  { id: '33', label: 'Factura electrónica', flow: 'dte' },
  { id: '34', label: 'Factura No afecta o exenta electrónica', flow: 'dte' },
  { id: '61', label: 'Nota de crédito electrónica', flow: 'nc' }
]

// -------------------- Vista 1: Selección de tipo de documento --------------------
const TipoDocumentoSelection = ({ onSelect }) => {
  return (
    <section className='bg-white p-6 rounded-2xl shadow space-y-4'>
      <h1 className='text-xl font-semibold mb-2'>Emitir DTE</h1>
      <p className='text-sm text-gray-600'>
        Selecciona el tipo de documento electrónico que deseas emitir.
      </p>

      <div className=''>
        {DOC_TYPES.map(doc => (
          <button
            key={doc.id}
            type='button'
            onClick={() => onSelect(doc)}
            className='relative w-full flex items-center px-4 py-1 bg-white text-left group'
          >
            <div className='flex items-center gap-2'>
              <span
                className='
                w-6 h-6 mr-2
                bg-[#5A97C7]
                text-white
                rounded-full
                flex items-center justify-center
                text-xs
              '
              >
                ➤
              </span>
              <span
                className='
                text-[#0066CC]
                font-medium
                group-hover:text-[#E66400]
                transition-colors
              '
              >
                {doc.label}
              </span>
            </div>
          </button>
        ))}
      </div>
    </section>
  )
}

export default function FacturaEmisionIndex({ onEmitSuccess, initialDocToClone }) {
  const { currentBusiness } = useGlobalContext()
  const facturasAPI = new FacturaAPI()

  // Vistas: 'SELECTION', 'FORM', 'PREVIEW', 'NC_LIST'
  const [view, setView] = useState('SELECTION')
  const [selectedDocType, setSelectedDocType] = useState(null) // { id, label, flow }
  const [docData, setDocData] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const xUseDefaults = true

  useEffect(() => {
    if (initialDocToClone) {
      const code = String(initialDocToClone?.dte_type_code ?? initialDocToClone?.dte_type?.code)
      if (code === '33' || code === '34') {
        setSelectedDocType({
          id: code,
          label: code === '33' ? 'Factura electrónica' : 'Factura No afecta o exenta electrónica',
          flow: 'dte'
        })
        setDocData({ initialClone: initialDocToClone })
        setView('FORM')
      }
    } else {
      // Si la prop se limpia (por ejemplo al cambiar de pestaña), la vista debe volver a inicio
      setSelectedDocType(null)
      setView('SELECTION')
      setDocData(null)
    }
  }, [initialDocToClone])

  // ---- Selección de tipo ----
  const handleSelectDocType = docType => {
    setSelectedDocType(docType)
    if (docType.flow === 'nc') {
      setView('NC_LIST')
    } else {
      setView('FORM')
    }
  }

  // ---- Al continuar desde el formulario (Form -> Preview) ----
  const handleFormContinue = ({ fullDoc, dteCode, formState }) => {
    setDocData({ fullDoc, dteCode, formState })
    setView('PREVIEW')
  }

  // ---- Totales para preview ----
  const calculateTotals = useCallback(doc => {
    if (!doc) return { subtotal: 0, iva: 0, total: 0 }

    const lines = doc.details || []

    const subtotal = lines.reduce((acc, d) => {
      const qty = Number(d.quantity) || 0
      const price = Number(d.unit_price) || 0
      const pct = Number(d.discount_percent) || 0

      const lineBruto = qty * price
      const desc = (lineBruto * pct) / 100
      return acc + (lineBruto - desc)
    }, 0)

    const isExenta = String(doc.dte_type?.code) === '34'
    const ivaRate = isExenta ? 0 : 0.19
    const iva = Math.round(subtotal * ivaRate)
    const total = subtotal + iva

    return { subtotal, iva, total }
  }, [])

  const handleEmit = async () => {
    if (!docData || !currentBusiness?.id) return

    const { fullDoc } = docData
    const payload = { documents: [fullDoc] }

    setIsSubmitting(true)

    // Pollea el estado ya emitido. Un error acá (red, CORS, etc.) NO significa que la
    // emisión haya fallado -- la emisión ya terminó antes de llegar a esta función.
    const pollFacturaEstado = async documentId => {
      const POLL_SUCCESS_STATUSES = ['issued', 'delivered', 'paid', 'unpaid']
      const POLL_ERROR_STATUSES = ['rejected', 'cancelled', 'issue_failed', 'invalid', 'permanent_error']
      const POLL_INTERVAL_MS = 4000
      const POLL_MAX_ATTEMPTS = 30 // ~2 minutos

      for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
        await new Promise(res => setTimeout(res, POLL_INTERVAL_MS))
        try {
          const statusRes = await facturasAPI.getEstadoLocal(documentId)
          const status = statusRes.data?.status

          if (POLL_SUCCESS_STATUSES.includes(status)) return { type: 'issued' }
          if (POLL_ERROR_STATUSES.includes(status)) return { type: 'rejected' }
        } catch (err) {
          console.error('Error consultando estado-local (el documento ya fue emitido):', err)
          // seguimos intentando; no cortamos el polling por un error puntual de red/CORS
        }
      }
      return { type: 'unknown' }
    }

    try {
      let documentId
      try {
        const res = await sileo.promise(facturasAPI.emitirFactura(currentBusiness.id, xUseDefaults, payload), {
          loading: { title: 'Enviando el documento al SII...' },
          success: { title: 'Aviso', description: 'Documento enviado, validando con el SII...' },
          error: { title: 'Error de Emisión', description: 'No se pudo completar la emisión del documento 🤯' }
        })
        documentId = res.data?.response_tupana?.documents?.[0]?.document?.id
        if (!documentId) throw new Error('No se pudo obtener el ID del documento en Tupana')
      } catch (emitError) {
        console.error('Error al emitir la factura:', emitError)
        return
      }

      // A partir de acá la emisión ya está confirmada; lo que sigue es solo
      // seguimiento de estado, nunca debe reportarse como "falló la emisión".
      const result = await pollFacturaEstado(documentId)

      if (result.type === 'issued') {
        sileo.success({
          title: 'Emisión Exitosa',
          description: (
            <div className='flex flex-col gap-1'>
              <span className='text-green-500/80 font-medium'>
                ¡Documento emitido correctamente! 👌
              </span>
            </div>
          )
        })
        if (onEmitSuccess) onEmitSuccess()
      } else if (result.type === 'rejected') {
        sileo.error({
          title: 'Documento Rechazado',
          description: 'Factura rechazada por el SII.'
        })
        if (onEmitSuccess) onEmitSuccess()
      } else {
        sileo.warning({
          title: 'Enviada, confirmación pendiente',
          description:
            'El documento se envió correctamente al SII. Todavía no pudimos confirmar el estado final -- revisa el listado en unos minutos.'
        })
        if (onEmitSuccess) onEmitSuccess()
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const totals = docData ? calculateTotals(docData.fullDoc) : { subtotal: 0, iva: 0, total: 0 }

  return (
    <div className='min-h-screen w-full p-6 flex justify-center bg-gray-50'>
      <div className='w-full max-w-6xl space-y-4'>
        {/* Vista: Selección */}
        {view === 'SELECTION' && <TipoDocumentoSelection onSelect={handleSelectDocType} />}

        {/* Vista: Formulario (Facturas) */}
        {view === 'FORM' && selectedDocType?.flow === 'dte' && (
          <FacturaFormSimplified
            onContinue={handleFormContinue}
            selectedDocType={selectedDocType}
            onBack={() => {
              setSelectedDocType(null)
              setView('SELECTION')
              setDocData(null)
            }}
            onReset={() => setDocData(null)}
            initialData={docData?.formState}
            initialClone={docData?.initialClone}
          />
        )}

        {/* Vista: Nota de Crédito (Listado directo) */}
        {view === 'NC_LIST' && selectedDocType?.flow === 'nc' && (
          <IndexNotaCredito
            currentBusiness={currentBusiness}
            onEmitSuccess={onEmitSuccess}
            onBack={() => {
              setSelectedDocType(null)
              setView('SELECTION')
              setDocData(null)
            }}
          />
        )}

        {/* Vista: Previsualización (Facturas) */}
        {view === 'PREVIEW' && selectedDocType?.flow === 'dte' && docData && (
          <PrevisualizacionFacturaIndividual
            document={docData.fullDoc}
            currentBusiness={currentBusiness}
            totals={totals}
            onBack={() => setView('FORM')}
            onEmit={() => setView('SIGN')}
            isSubmitting={isSubmitting}
          />
        )}

        {/* Vista: Firma y Emisión */}
        {view === 'SIGN' && selectedDocType?.flow === 'dte' && docData && (
          <FirmaEmisionFactura
            document={docData.fullDoc}
            totals={totals}
            onBack={() => setView('PREVIEW')}
            onEmit={handleEmit}
            isSubmitting={isSubmitting}
          />
        )}
      </div>
    </div>
  )
}
